import React, { memo, useState, useEffect, useCallback } from 'react';

// Escalas de tamaño de fuente (A− … A+). 1 = tamaño base.
const FONT_STEPS = [0.875, 1, 1.125, 1.25];
const DEFAULT_INDEX = 1;
const STORAGE_KEY = 'a11y-prefs';

// Etiqueta que aparece a la izquierda del botón en hover/focus (estados del manual).
const Label = ({ children }) => (
  <span className="pointer-events-none absolute right-full top-1/2 -translate-y-1/2 mr-px hidden group-hover:block group-focus-visible:block whitespace-nowrap bg-cobalt-hover text-white text-sm font-semibold px-3 py-2 rounded-l-md shadow-md">
    {children}
  </span>
);

const BTN =
  'group relative flex items-center justify-center w-12 h-12 min-w-[44px] min-h-[44px] text-white ' +
  'hover:bg-cobalt-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white ' +
  'disabled:opacity-40 disabled:cursor-not-allowed transition-colors';

/**
 * Barra de accesibilidad — GOV.CO Kit UI v9.2 (Componente transversal).
 * Funciones: cambiar contraste, aumentar y reducir el tamaño de la fuente.
 * Autocontenida: maneja su estado y lo persiste; alterna `html.a11y-contrast`
 * (modo de inversión definido en index.css) y fija `--a11y-font-scale` que el
 * contenido principal lee con `zoom`. `data-a11y-keep-color` evita que la barra
 * se invierta en modo contraste para seguir siendo usable.
 */
const AccessibilityBar = () => {
  const [fontIndex, setFontIndex] = useState(DEFAULT_INDEX);
  const [highContrast, setHighContrast] = useState(false);

  // Cargar preferencias persistidas (se mantienen entre páginas y sesiones).
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      if (
        typeof saved.fontIndex === 'number' &&
        saved.fontIndex >= 0 &&
        saved.fontIndex < FONT_STEPS.length
      ) {
        setFontIndex(saved.fontIndex);
      }
      if (typeof saved.highContrast === 'boolean') {
        setHighContrast(saved.highContrast);
      }
    } catch {
      /* localStorage no disponible o JSON inválido: usar valores por defecto */
    }
  }, []);

  // Aplicar la escala de fuente (la lee `main { zoom: var(--a11y-font-scale) }`).
  useEffect(() => {
    document.documentElement.style.setProperty('--a11y-font-scale', String(FONT_STEPS[fontIndex]));
  }, [fontIndex]);

  // Aplicar/quitar el modo de alto contraste (inversión en index.css).
  useEffect(() => {
    document.documentElement.classList.toggle('a11y-contrast', highContrast);
  }, [highContrast]);

  // Persistir cualquier cambio.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ fontIndex, highContrast }));
    } catch {
      /* ignorar si localStorage está bloqueado */
    }
  }, [fontIndex, highContrast]);

  const increaseFont = useCallback(
    () => setFontIndex((i) => Math.min(i + 1, FONT_STEPS.length - 1)),
    []
  );
  const decreaseFont = useCallback(() => setFontIndex((i) => Math.max(i - 1, 0)), []);
  const toggleContrast = useCallback(() => setHighContrast((v) => !v), []);
  const canIncrease = fontIndex < FONT_STEPS.length - 1;
  const canDecrease = fontIndex > 0;

  return (
    <aside
      data-a11y-keep-color
      aria-label="Barra de accesibilidad"
      className="hidden lg:flex fixed right-0 top-1/2 -translate-y-1/2 z-[1500] flex-col rounded-l-lg bg-cobalt shadow-[0_4px_16px_rgba(0,0,0,0.25)]"
    >
      {/* Contraste */}
      <button
        type="button"
        onClick={toggleContrast}
        aria-pressed={highContrast}
        aria-label="Cambiar el contraste de color"
        className={`${BTN} rounded-tl-lg border-b border-white/20 ${highContrast ? 'bg-cobalt-hover' : ''}`}
      >
        <Label>Contraste</Label>
        <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
          <path d="M12 3a9 9 0 0 1 0 18V3z" fill="currentColor" />
        </svg>
      </button>

      {/* Aumentar letra (A+) — destacado, como en el manual */}
      <button
        type="button"
        onClick={increaseFont}
        disabled={!canIncrease}
        aria-label="Aumentar el tamaño de la fuente"
        className={`${BTN} border-b border-white/20`}
      >
        <Label>Aumentar letra</Label>
        <span aria-hidden="true" className="font-heading font-bold leading-none flex items-start text-[1.3em]">
          A<span className="text-[0.6em] ml-px mt-px">+</span>
        </span>
      </button>

      {/* Reducir letra (A−) */}
      <button
        type="button"
        onClick={decreaseFont}
        disabled={!canDecrease}
        aria-label="Reducir el tamaño de la fuente"
        className={`${BTN} rounded-bl-lg`}
      >
        <Label>Reducir letra</Label>
        <span aria-hidden="true" className="font-heading font-bold leading-none flex items-start text-[0.95em]">
          A<span className="text-[0.6em] ml-px mt-px">−</span>
        </span>
      </button>
    </aside>
  );
};

export default memo(AccessibilityBar);
